import { Link } from 'react-router-dom'

import { manifests } from '@senses/games'

import { Alert, Empty, Spinner } from '../components/ui.tsx'
import { api } from '../lib/api.ts'
import { useAuth } from '../lib/auth.tsx'
import { useAsync } from '../lib/hooks.ts'
import { formatRelative, plural } from '../lib/format.ts'

/** Rozcestník trenéra: kdo trénuje, kdo vypadl a co je po ruce. */
export function StaffDashboard(): React.JSX.Element {
  const { user } = useAuth()

  const clients = useAsync(() => api.users.list('client'), [])
  const programs = useAsync(() => api.programs.list(), [])

  const list = clients.data?.users ?? []
  const activeClients = list.filter((c) => c.active)

  return (
    <main className="page">
      <div className="page__header">
        <div>
          <h1>Přehled</h1>
          <p>Vítej zpět, {user?.name.split(' ')[0]}.</p>
        </div>
        <div className="page__actions">
          <Link to="/klienti" className="btn">
            Přidat klienta
          </Link>
          <Link to="/programy/novy" className="btn btn--primary">
            Nový program
          </Link>
        </div>
      </div>

      {clients.error && <Alert>{clients.error}</Alert>}

      <div className="stats">
        <div className="stat">
          <div className="stat__label">Aktivní klienti</div>
          <div className="stat__value">{activeClients.length}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Programy</div>
          <div className="stat__value">{programs.data?.programs.length ?? '—'}</div>
        </div>
        <div className="stat">
          <div className="stat__label">Dostupné hry</div>
          <div className="stat__value">{manifests.length}</div>
        </div>
      </div>

      <section className="section">
        <div className="section__title">
          <h2>Klienti</h2>
          <Link to="/klienti" className="btn btn--ghost btn--sm">
            Spravovat
          </Link>
        </div>

        {clients.loading ? (
          <Spinner />
        ) : activeClients.length === 0 ? (
          <Empty title="Zatím nemáš žádné klienty">
            <p>
              Klienta založíš v sekci <Link to="/klienti">Klienti</Link>. Pak mu přiřadíš program
              a on se přihlásí vlastními údaji.
            </p>
          </Empty>
        ) : (
          <div className="grid">
            {activeClients.map((client) => (
              <ClientTile key={client.id} id={client.id} name={client.name} email={client.email} />
            ))}
          </div>
        )}
      </section>
    </main>
  )
}

function ClientTile({
  id,
  name,
  email,
}: {
  id: number
  name: string
  email: string
}): React.JSX.Element {
  // Poslední aktivita se dotahuje po dlaždicích; každý klient má vlastní dotaz,
  // ale jde o jeden řádek z indexu a seznamy jsou malé.
  const recent = useAsync(() => api.sessions.list({ userId: id, limit: 1 }), [id])
  const last = recent.data?.sessions[0]

  const assignments = useAsync(() => api.assignments.list(id, true), [id])
  const count = assignments.data?.assignments.length ?? 0

  return (
    <Link to={`/klienti/${id}`} className="card card--link">
      <h3>{name}</h3>
      <p className="dim" style={{ fontSize: '0.85rem' }}>
        {email}
      </p>

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', flexWrap: 'wrap' }}>
        <span className="badge">
          {count} {plural(count, 'program', 'programy', 'programů')}
        </span>
        <span className={`badge ${last ? 'badge--success' : 'badge--warning'}`}>
          {last ? formatRelative(last.finishedAt) : 'netrénoval'}
        </span>
      </div>
    </Link>
  )
}
